import { BehaviorSubject, lastValueFrom, Observable, of } from "rxjs";
import { inject, Injectable } from "@angular/core";
import { tap, catchError } from "rxjs/operators";
import { AuthAppService } from "@modules/auth";

/**
 * LanguageService is responsible for managing the user's language preference.
 * It allows setting and getting the language, and notifies subscribers when the language changes.
 */
@Injectable({
    providedIn: 'root'
})
export class LanguageService {
    private authService = inject(AuthAppService);
    private language: string = 'fr';
    private isInitialized = false;

    public langChanged$ = new BehaviorSubject<string>(this.language);

    /**
     * 🔧 NOUVELLE MÉTHODE : Initialisation asynchrone
     */
    public async initializeLanguage(): Promise<string> {
        if (this.isInitialized) {
            return this.language;
        }

        try {
            // 1. Vérifier d'abord le localStorage
            const storedLanguage = localStorage.getItem('userLanguage');
            if (storedLanguage && ['en', 'fr'].includes(storedLanguage)) {
                this.language = storedLanguage;
                this.langChanged$.next(this.language);
                this.isInitialized = true;
                return this.language;
            }

            // 2. Si pas de langue stockée, vérifier la langue du navigateur
            // Get language from browser configuration
            if (navigator.language) {
                const browserLang = navigator.language.split('-')[0]; // Ex: 'fr-FR' -> 'fr'
                if (['en', 'fr'].includes(browserLang)) {
                    this.language = browserLang;
                    localStorage.setItem('userLanguage', this.language);
                    this.langChanged$.next(this.language);
                    this.isInitialized = true;
                    return this.language;
                }
            }

            // 3. Si pas de langue stockée, récupérer depuis l'API
            const langResponse = await lastValueFrom(this.authService.getLang());

            if (langResponse?.lang && ['en', 'fr'].includes(langResponse.lang)) {
                this.language = langResponse.lang;
                localStorage.setItem('userLanguage', this.language);
            } else {
                // 3. Fallback vers français si l'API retourne quelque chose d'invalide
                this.language = 'fr';
                localStorage.setItem('userLanguage', 'fr');
            }

            this.langChanged$.next(this.language);
            this.isInitialized = true;
            return this.language;

        } catch (error) {
            console.error('🚨 Error fetching language from API, using default:', error);
            this.language = 'fr';
            localStorage.setItem('userLanguage', 'fr');
            this.langChanged$.next(this.language);
            this.isInitialized = true;
            return this.language;
        }
    }

    /**
     * 🔧 MÉTHODE OPTIMISÉE : Synchrone, pas d'appel API
     */
    public getLanguage(): string {
        // Si pas encore initialisé, retourner la langue par défaut
        if (!this.isInitialized) {
            const storedLanguage = localStorage.getItem('userLanguage');
            if (storedLanguage && ['en', 'fr'].includes(storedLanguage)) {
                this.language = storedLanguage;
            }
        }
        return this.language;
    }

    /**
     * 🔧 MÉTHODE OPTIMISÉE : Avec gestion d'erreur
     */
    public setLanguage(language: string): void {
        if (!['en', 'fr'].includes(language)) {
            console.error('🚨 Invalid language:', language);
            return;
        }

        if (language === this.language) {
            return;
        }

        this.language = language;
        localStorage.setItem('userLanguage', language);
        this.langChanged$.next(language);

        // Appel API en arrière-plan (ne pas attendre le résultat)
        this.authService.setLang(language).pipe(
            catchError(error => {
                console.error('🚨 Error saving language to API:', error);
                return of(null); // Continue même si l'API échoue
            })
        ).subscribe();
    }

    /**
     * 🔧 NOUVELLE MÉTHODE : Forcer une langue spécifique
     */
    public forceLanguage(language: string): void {
        if (!['en', 'fr'].includes(language)) {
            console.error('🚨 Invalid language:', language);
            return;
        }
        this.language = language;
        localStorage.setItem('userLanguage', language);
        this.langChanged$.next(language);
        this.isInitialized = true;
    }
}